import React, { useEffect } from 'react'
import roleApi from '../api/role/RoleApi'
import { RoleProcessingStatus } from '../api/role/models/RoleDto'
import FlashNumber from './FlashNumber' // adjust path as necessary
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faRotate } from '@fortawesome/free-solid-svg-icons'
import clsx from 'clsx'
import Button from './Button'
import { toast } from 'sonner'
import Note from './Note'
import { NoteType } from '../enums/NoteType'

type Props = {
    roleId: string
    roleFinishedCallback: (status: RoleProcessingStatus) => void
    className?: string
}

const steps = [
    {
        status: RoleProcessingStatus.GATHERING_CANDIDATES,
        label: 'Gathering Candidates',
    },
    {
        status: RoleProcessingStatus.CALC_PROXIMITY_BOUNDARIES,
        label: 'Calculating Proximity Boundaries',
    },
    {
        status: RoleProcessingStatus.GENERATING_NETWORK,
        label: 'Generating Network',
    },
    {
        status: RoleProcessingStatus.CALC_JOB_RELEVANCE,
        label: 'Calculating Job Relevance',
    },
    {
        status: RoleProcessingStatus.FINDING_BEST_CANDIDATES,
        label: 'Ranking Candidates',
    },
    { status: RoleProcessingStatus.COMPLETE, label: 'Complete' },
]

function RoleProcessingStatusUi({
    roleId,
    roleFinishedCallback,
    className,
}: Props): React.JSX.Element {
    const [status, setStatus] = React.useState<
        RoleProcessingStatus | undefined
    >(undefined)
    const [candidatePoolCount, setCandidatePoolCount] = React.useState<
        number | undefined
    >(undefined)
    const [closenessCalculationProgress, setClosenessCalculationProgress] =
        React.useState<number | undefined>(undefined)
    const [proximityBoundaryProgress, setProximityBoundaryProgress] =
        React.useState<number | undefined>(undefined)
    const [
        jobRelevanceCalculationProgress,
        setJobRelevanceCalculationProgress,
    ] = React.useState<number | undefined>(undefined)
    const [candidateRankingProgress, setCandidateRankingProgress] =
        React.useState<number | undefined>(undefined)
    const [shouldShowRestart, setShouldShowRestart] = React.useState(false)

    useEffect(() => {
        const fetchRole = async () => {
            const { role } = await roleApi.getLite({
                roleId,
                includeHiringTeamIds: false,
                includeNetworkLinkIds: false,
                includeCompanyLinkIds: false,
            })

            setStatus(role.status)
            setCandidatePoolCount(role.candidatePoolCount)
            setClosenessCalculationProgress(role.closenessCalculationProgress)
            setProximityBoundaryProgress(role.proximityBoundaryProgress)
            setJobRelevanceCalculationProgress(
                role.jobRelevanceCalculationProgress,
            )
            setCandidateRankingProgress(role.candidateRankingProgress)
            if (role.startDate) {
                const nowUtc = new Date(Date.now())
                const startUtc = new Date(role.startDate)

                const diffInMs = nowUtc.getTime() - startUtc.getTime()
                // 10 mins
                setShouldShowRestart(diffInMs > 10 * 60 * 1000)
            } else {
                setShouldShowRestart(false)
            }

            if (
                role.status === RoleProcessingStatus.COMPLETE ||
                role.status === RoleProcessingStatus.ERROR
            ) {
                clearInterval(interval)
                roleFinishedCallback(role.status)
            }
        }
        const interval = setInterval(fetchRole, 1500)
        fetchRole()
        return () => clearInterval(interval)
    }, [roleId, roleFinishedCallback])

    async function processRole() {
        if (!roleId) {
            return
        }

        try {
            await roleApi.processRole({ roleId })
        } catch (error) {
            console.error(error)
            toast.error(
                `There was a problem processing the role, please try again.`,
                {
                    duration: 5000,
                },
            )
        }
    }

    // Determine the active step index.
    const currentStepIndex = steps.findIndex((step) => step.status === status)
    const isOverallComplete = status === RoleProcessingStatus.COMPLETE

    // Return a JSX element with FlashNumber for each numeric value.
    const getLabelWithProgress = (
        stepStatus: RoleProcessingStatus,
        label: string,
    ) => {
        if (stepStatus === RoleProcessingStatus.GATHERING_CANDIDATES) {
            return (
                <>
                    {label}
                    <div className="flex">
                        <FlashNumber value={candidatePoolCount ?? 0} />
                        <span className="ml-1">candidates found</span>
                    </div>
                </>
            )
        } else if (
            stepStatus === RoleProcessingStatus.CALC_PROXIMITY_BOUNDARIES
        ) {
            return (
                <>
                    {label}{' '}
                    <div className="flex">
                        <FlashNumber value={proximityBoundaryProgress ?? 0} />
                        <span className="mx-1">/</span>
                        <FlashNumber value={candidatePoolCount ?? 0} />
                    </div>
                </>
            )
        } else if (stepStatus === RoleProcessingStatus.GENERATING_NETWORK) {
            return (
                <>
                    {label}{' '}
                    <div className="flex">
                        <FlashNumber
                            value={closenessCalculationProgress ?? 0}
                        />
                        <span className="mx-1">/</span>
                        <FlashNumber value={candidatePoolCount ?? 0} />
                    </div>
                </>
            )
        } else if (stepStatus === RoleProcessingStatus.CALC_JOB_RELEVANCE) {
            return (
                <>
                    {label}{' '}
                    <div className="flex">
                        <FlashNumber
                            value={jobRelevanceCalculationProgress ?? 0}
                        />
                        <span className="mx-1">/</span>
                        <FlashNumber value={candidatePoolCount ?? 0} />
                    </div>
                </>
            )
        } else if (
            stepStatus === RoleProcessingStatus.FINDING_BEST_CANDIDATES
        ) {
            return (
                <>
                    {label}{' '}
                    <div className="flex">
                        <FlashNumber value={candidateRankingProgress ?? 0} />
                        <span className="mx-1">/</span>
                        <FlashNumber value={candidatePoolCount ?? 0} />
                    </div>
                </>
            )
        } else if (stepStatus === RoleProcessingStatus.COMPLETE) {
            return label
        }
        return label
    }

    const classes = clsx('', className)

    return (
        <div className={classes}>
            {steps.map((step, index) => {
                let circleContent = null
                if (isOverallComplete || currentStepIndex > index) {
                    // Completed steps get a green circle with a checkmark.
                    circleContent = (
                        // <span className="text-white text-xl">&#10003;</span>
                        <span className="text-white text-xl">
                            <FontAwesomeIcon icon={faCheck} />
                        </span>
                    )
                } else if (currentStepIndex === index) {
                    // Active step: show spinner.
                    circleContent = (
                        <div className="w-6 h-6 border-4 border-t-white border-gray-300 rounded-full animate-spin" />
                    )
                }
                return (
                    <div key={step.status} className="flex items-center">
                        {/* Left column: connectors and circle */}
                        <div className="flex flex-col items-center">
                            {index !== 0 && (
                                <div className="w-[4px] bg-gray-300 h-2" />
                            )}
                            <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                    isOverallComplete ||
                                    currentStepIndex > index
                                        ? 'bg-green-500'
                                        : 'bg-gray-300'
                                }`}
                            >
                                {circleContent}
                            </div>
                            {index !== steps.length - 1 && (
                                <div className="w-[4px] bg-gray-300 h-2" />
                            )}
                        </div>
                        {/* Right column: conditionally render label */}
                        {(isOverallComplete || currentStepIndex >= index) && (
                            <div className="ml-4">
                                <span className="text-sm">
                                    {getLabelWithProgress(
                                        step.status,
                                        step.label,
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                )
            })}
            {shouldShowRestart && (
                <Note type={NoteType.WARNING} containerClassNames="mt-6">
                    The network is taking longer than expected to generate.
                    <Button
                        style="secondary"
                        type="button"
                        onClick={() => {
                            processRole()
                        }}
                        classes="mt-4"
                    >
                        <FontAwesomeIcon icon={faRotate} className="mr-2" />
                        Regenerate Network
                    </Button>
                </Note>
            )}
        </div>
    )
}

export default RoleProcessingStatusUi
